import React from 'react';
import {
	AbsoluteFill,
	useCurrentFrame,
	useVideoConfig,
	interpolate,
	Easing,
} from 'remotion';

/**
 * 看不见的力量：电磁场
 * 四阶段叙事：万物之始 → 无形之线 → 相互吸引 → 宇宙之力
 * 讲述正负电荷从诞生、产生场力线、相互吸引到释放能量的完整故事
 */
export const ElectromagneticFieldSimulation: React.FC = () => {
	const frame = useCurrentFrame();
	const { width, height, durationInFrames } = useVideoConfig();

	const totalProgress = frame / durationInFrames;

	const phaseProgress = (phaseStart: number, phaseDuration: number) => {
		const p = (totalProgress - phaseStart) / phaseDuration;
		return Math.max(0, Math.min(1, p));
	};

	const phase1 = phaseProgress(0, 0.25);
	const phase2 = phaseProgress(0.25, 0.25);
	const phase3 = phaseProgress(0.5, 0.25);
	const phase4 = phaseProgress(0.75, 0.25);

	const S = 1.5;
	const TS = 1.6;

	const cx = width / 2;
	const cy = height / 2;

	// --- 背景 ---
	const bgHue = interpolate(totalProgress, [0, 0.5, 1], [240, 230, 260], {
		extrapolateRight: 'clamp',
	});
	const bgLight = interpolate(totalProgress, [0, 0.5, 0.85, 1], [4, 8, 12, 6], {
		extrapolateRight: 'clamp',
	});

	// --- Phase 1: 电荷诞生 ---
	// 中心光点出现，然后分裂为正负电荷向两侧飞出
	const birthFlash = interpolate(phase1, [0, 0.2, 0.4], [0, 1, 0], {
		extrapolateRight: 'clamp',
	});
	const maxSeparation = 320 * S;
	const chargeSeparation = interpolate(
		totalProgress,
		[0, 0.25, 0.5, 0.75, 1],
		[0, maxSeparation, maxSeparation, maxSeparation * 0.35, maxSeparation * 0.15],
		{ extrapolateRight: 'clamp', easing: Easing.inOut(Easing.ease) },
	);

	const positiveX = cx - chargeSeparation;
	const negativeX = cx + chargeSeparation;

	const chargeScale = interpolate(phase1, [0.3, 0.6], [0, 1], {
		extrapolateRight: 'clamp',
	});
	const chargeSize = 72 * S;

	// 电荷呼吸脉动
	const breathe = Math.sin(frame * 0.08) * 0.06;

	// --- Phase 2: 电场线形成 ---
	const numFieldLines = 12;
	const fieldLineOpacity = interpolate(phase2, [0, 0.4], [0, 1], {
		extrapolateRight: 'clamp',
	});

	const fieldLines: React.ReactNode[] = [];
	for (let i = 0; i < numFieldLines; i++) {
		const angle = (i / numFieldLines) * Math.PI * 2;
		const lineGrow = interpolate(phase2, [i * 0.04, i * 0.04 + 0.5], [0, 1], {
			extrapolateRight: 'clamp',
			extrapolateLeft: 'clamp',
		});

		if (lineGrow <= 0) continue;

		const startR = 45 * S;
		const sx = positiveX + Math.cos(angle) * startR;
		const sy = cy + Math.sin(angle) * startR;
		const ex = negativeX + Math.cos(angle + Math.PI) * startR;
		const ey = cy + Math.sin(angle + Math.PI) * startR;

		const curvature = Math.sin(angle) * 120 * S;
		const midX = (sx + ex) / 2;
		const midY = (sy + ey) / 2 + curvature;

		const drawLen = lineGrow;
		const pathD = `M ${sx} ${sy} Q ${midX} ${midY} ${ex} ${ey}`;

		const flowOffset = (frame * 0.015 + i * 0.08) % 1;
		const particleT = flowOffset;
		const px = (1 - particleT) * (1 - particleT) * sx + 2 * (1 - particleT) * particleT * midX + particleT * particleT * ex;
		const py = (1 - particleT) * (1 - particleT) * sy + 2 * (1 - particleT) * particleT * midY + particleT * particleT * ey;

		const intensityBoost = interpolate(totalProgress, [0.5, 0.85], [1, 2.5], {
			extrapolateRight: 'clamp',
			extrapolateLeft: 'clamp',
		});

		fieldLines.push(
			<g key={`fl-${i}`}>
				<path
					d={pathD}
					stroke={`rgba(255, 220, 130, ${0.15 * fieldLineOpacity * intensityBoost})`}
					strokeWidth={1.5 * S * intensityBoost}
					fill="none"
					strokeDasharray="8,6"
					strokeDashoffset={-frame * 1.2}
					pathLength={1}
					strokeLinecap="round"
					style={{
						strokeDasharray: `${drawLen}, 1`,
						transition: 'none',
					}}
				/>
				{lineGrow > 0.3 && (
					<circle
						cx={px}
						cy={py}
						r={3 * S * intensityBoost}
						fill={`rgba(255, 230, 150, ${0.8 * fieldLineOpacity})`}
					/>
				)}
			</g>,
		);
	}

	// --- Phase 3: 相互吸引，场增强 ---
	const attractionParticles: React.ReactNode[] = [];
	if (phase3 > 0) {
		const particleCount = Math.floor(interpolate(phase3, [0, 1], [10, 60], { extrapolateRight: 'clamp' }));
		for (let i = 0; i < particleCount; i++) {
			const seed = i * 137.508;
			const t = ((frame * 0.02 + seed * 0.01) % 1);
			const spreadAngle = ((seed * 2.39996) % (Math.PI * 2));
			const spreadR = interpolate(t, [0, 1], [0, 60 * S]);

			const baseX = positiveX + (negativeX - positiveX) * t;
			const baseY = cy + Math.sin(spreadAngle) * spreadR;

			const size = (2 + Math.sin(seed) * 1.5) * S;
			const opacity = interpolate(t, [0, 0.15, 0.85, 1], [0, 0.9, 0.9, 0], {
				extrapolateRight: 'clamp',
			});

			const r = interpolate(t, [0, 1], [255, 120]);
			const g = interpolate(t, [0, 1], [130, 190]);
			const b = interpolate(t, [0, 1], [100, 255]);

			attractionParticles.push(
				<div
					key={`ap-${i}`}
					style={{
						position: 'absolute',
						left: baseX - size / 2,
						top: baseY - size / 2,
						width: size,
						height: size,
						borderRadius: '50%',
						background: `rgb(${r}, ${g}, ${b})`,
						opacity: opacity * phase3,
						boxShadow: `0 0 ${size * 3}px rgba(${r}, ${g}, ${b}, 0.6)`,
					}}
				/>,
			);
		}
	}

	// 围绕电荷的轨道粒子
	const orbitParticles: React.ReactNode[] = [];
	const orbitCount = 24;
	const orbitVisibility = interpolate(totalProgress, [0.2, 0.35], [0, 1], {
		extrapolateRight: 'clamp',
	});
	for (let i = 0; i < orbitCount; i++) {
		const isPositive = i < orbitCount / 2;
		const localI = isPositive ? i : i - orbitCount / 2;
		const centerX = isPositive ? positiveX : negativeX;

		const orbitAngle = (localI / (orbitCount / 2)) * Math.PI * 2 + frame * (isPositive ? 0.06 : -0.06);
		const orbitR = (52 + Math.sin(localI * 1.8 + frame * 0.04) * 15) * S;
		const ox = centerX + Math.cos(orbitAngle) * orbitR;
		const oy = cy + Math.sin(orbitAngle) * orbitR;

		const size = (3 + Math.sin(localI * 2.5) * 1) * S;
		const color = isPositive ? 'rgba(255, 120, 120, 0.7)' : 'rgba(120, 180, 255, 0.7)';

		orbitParticles.push(
			<div
				key={`orbit-${i}`}
				style={{
					position: 'absolute',
					left: ox - size / 2,
					top: oy - size / 2,
					width: size,
					height: size,
					borderRadius: '50%',
					background: color,
					boxShadow: `0 0 ${size * 2}px ${color}`,
					opacity: orbitVisibility * chargeScale,
				}}
			/>,
		);
	}

	// --- Phase 4: 能量爆发 ---
	const burstProgress = interpolate(phase4, [0.3, 0.6], [0, 1], {
		extrapolateRight: 'clamp',
		extrapolateLeft: 'clamp',
	});
	const burstRadius = interpolate(burstProgress, [0, 1], [0, 350 * S]);
	const burstOpacity = interpolate(burstProgress, [0, 0.3, 1], [0, 0.6, 0], {
		extrapolateRight: 'clamp',
	});

	const burstRings: React.ReactNode[] = [];
	for (let i = 0; i < 3; i++) {
		const delay = i * 0.12;
		const ringP = Math.max(0, burstProgress - delay);
		const ringR = ringP * 350 * S;
		const ringOp = interpolate(ringP, [0, 0.2, 1], [0, 0.5, 0], { extrapolateRight: 'clamp' });

		burstRings.push(
			<div
				key={`burst-ring-${i}`}
				style={{
					position: 'absolute',
					left: cx - ringR,
					top: cy - ringR,
					width: ringR * 2,
					height: ringR * 2,
					borderRadius: '50%',
					border: `${2 * S}px solid rgba(255, 220, 160, ${ringOp})`,
					pointerEvents: 'none',
				}}
			/>,
		);
	}

	// --- 脉冲波纹 ---
	const waves: React.ReactNode[] = [];
	if (chargeScale > 0.5) {
		for (let i = 0; i < 3; i++) {
			const waveP = (frame * 0.018 + i * 0.33) % 1;
			const waveR = interpolate(waveP, [0, 1], [40 * S, 130 * S]);
			const waveOp = interpolate(waveP, [0, 1], [0.4, 0]);

			waves.push(
				<React.Fragment key={`wave-${i}`}>
					<div
						style={{
							position: 'absolute',
							left: positiveX - waveR,
							top: cy - waveR,
							width: waveR * 2,
							height: waveR * 2,
							borderRadius: '50%',
							border: `${1.5 * S}px solid rgba(255, 120, 120, ${waveOp * chargeScale})`,
							pointerEvents: 'none',
						}}
					/>
					<div
						style={{
							position: 'absolute',
							left: negativeX - waveR,
							top: cy - waveR,
							width: waveR * 2,
							height: waveR * 2,
							borderRadius: '50%',
							border: `${1.5 * S}px solid rgba(120, 180, 255, ${waveOp * chargeScale})`,
							pointerEvents: 'none',
						}}
					/>
				</React.Fragment>,
			);
		}
	}

	// --- 背景星尘 ---
	const stardust: React.ReactNode[] = [];
	for (let i = 0; i < 40; i++) {
		const seed = i * 97.31;
		const sx = ((seed * 7.13) % width);
		const sy = ((seed * 3.77) % height);
		const drift = Math.sin(frame * 0.01 + seed) * 8;
		const flickerOp = 0.15 + Math.sin(frame * 0.04 + seed * 0.5) * 0.1;
		const size = (1.5 + (seed % 2)) * S;

		stardust.push(
			<div
				key={`star-${i}`}
				style={{
					position: 'absolute',
					left: sx + drift,
					top: sy,
					width: size,
					height: size,
					borderRadius: '50%',
					background: 'rgba(200, 210, 255, 0.8)',
					opacity: flickerOp,
				}}
			/>,
		);
	}

	// --- 背景网格 ---
	const gridOpacity = interpolate(totalProgress, [0, 0.1, 0.9, 1], [0, 0.035, 0.035, 0], {
		extrapolateRight: 'clamp',
	});

	// --- 叙事文本 ---
	type NarrativeItem = { text: string; sub: string; start: number; end: number };
	const narratives: NarrativeItem[] = [
		{ text: '万物之始', sub: '在宇宙的深处，一次微小的涨落诞生了两种力量', start: 0, end: 0.25 },
		{ text: '无形之线', sub: '它们之间，看不见的电场线悄然连接', start: 0.25, end: 0.5 },
		{ text: '相互吸引', sub: '异性相吸——电荷在场力的驱动下彼此靠近', start: 0.5, end: 0.75 },
		{ text: '宇宙之力', sub: '电磁力，维系原子、点亮星辰的基本力量', start: 0.75, end: 1 },
	];

	const currentNarrative = narratives.find(
		(n) => totalProgress >= n.start && totalProgress < n.end,
	) ?? narratives[narratives.length - 1];

	const narrativeLocalP = (totalProgress - currentNarrative.start) / (currentNarrative.end - currentNarrative.start);
	const titleOpacity = interpolate(narrativeLocalP, [0, 0.15, 0.8, 1], [0, 1, 1, 0], {
		extrapolateRight: 'clamp',
	});
	const subtitleOpacity = interpolate(narrativeLocalP, [0.1, 0.25, 0.8, 1], [0, 1, 1, 0], {
		extrapolateRight: 'clamp',
	});
	const titleY = interpolate(narrativeLocalP, [0, 0.15], [15, 0], {
		extrapolateRight: 'clamp',
	});

	// --- 顶部总标题 ---
	const mainTitleOp = interpolate(totalProgress, [0, 0.06, 0.92, 1], [0, 0.7, 0.7, 0], {
		extrapolateRight: 'clamp',
	});

	// --- 底部进度指示器 ---
	const phaseIndex = Math.min(3, Math.floor(totalProgress * 4));
	const phaseNames = ['诞生', '连接', '吸引', '绽放'];

	return (
		<AbsoluteFill
			style={{
				background: `hsl(${bgHue}, 30%, ${bgLight}%)`,
				overflow: 'hidden',
			}}
		>
			{/* 背景网格 */}
			<svg
				style={{ position: 'absolute', width: '100%', height: '100%', opacity: gridOpacity }}
			>
				{Array.from({ length: Math.ceil(width / 60) }).map((_, i) => (
					<line
						key={`gv-${i}`}
						x1={i * 60} y1={0} x2={i * 60} y2={height}
						stroke="rgba(150,170,255,0.5)" strokeWidth={0.5}
					/>
				))}
				{Array.from({ length: Math.ceil(height / 60) }).map((_, i) => (
					<line
						key={`gh-${i}`}
						x1={0} y1={i * 60} x2={width} y2={i * 60}
						stroke="rgba(150,170,255,0.5)" strokeWidth={0.5}
					/>
				))}
			</svg>

			{/* 背景星尘 */}
			{stardust}

			{/* 中心诞生闪光 */}
			{birthFlash > 0 && (
				<div
					style={{
						position: 'absolute',
					left: cx - 150 * S,
					top: cy - 150 * S,
					width: 300 * S,
					height: 300 * S,
						borderRadius: '50%',
						background: `radial-gradient(circle, rgba(255,255,240,${birthFlash * 0.9}) 0%, transparent 70%)`,
						pointerEvents: 'none',
					}}
				/>
			)}

			{/* 脉冲波纹 */}
			{waves}

			{/* 电场线 */}
			{fieldLineOpacity > 0 && (
				<svg style={{ position: 'absolute', width: '100%', height: '100%' }}>
					{fieldLines}
				</svg>
			)}

			{/* 吸引粒子流 */}
			{attractionParticles}

			{/* 轨道粒子 */}
			{orbitParticles}

			{/* 正电荷 */}
			<div
				style={{
					position: 'absolute',
					left: positiveX - chargeSize / 2,
					top: cy - chargeSize / 2,
					width: chargeSize,
					height: chargeSize,
					borderRadius: '50%',
				background: 'radial-gradient(circle, rgba(255,100,100,0.9) 0%, rgba(255,60,60,0.15) 100%)',
				border: `${2.5 * S}px solid rgba(255,120,120,0.9)`,
				boxShadow: `0 0 ${(25 + breathe * 100) * S}px rgba(255,100,100,0.7)`,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				fontSize: 38 * TS,
					color: '#fff',
					fontWeight: 'bold',
					transform: `scale(${chargeScale * (1 + breathe)})`,
					opacity: chargeScale,
				}}
			>
				+
			</div>

			{/* 负电荷 */}
			<div
				style={{
					position: 'absolute',
					left: negativeX - chargeSize / 2,
					top: cy - chargeSize / 2,
					width: chargeSize,
					height: chargeSize,
					borderRadius: '50%',
				background: 'radial-gradient(circle, rgba(100,160,255,0.9) 0%, rgba(60,120,255,0.15) 100%)',
				border: `${2.5 * S}px solid rgba(120,180,255,0.9)`,
				boxShadow: `0 0 ${(25 + breathe * 100) * S}px rgba(100,160,255,0.7)`,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				fontSize: 38 * TS,
					color: '#fff',
					fontWeight: 'bold',
					transform: `scale(${chargeScale * (1 + breathe)})`,
					opacity: chargeScale,
				}}
			>
				−
			</div>

			{/* 电荷标签 */}
			{chargeScale > 0.8 && phase4 < 0.5 && (
				<>
					<div
						style={{
							position: 'absolute',
							left: positiveX - 50,
							top: cy + chargeSize / 2 + 14,
							fontSize: 13 * TS,
							color: 'rgba(255,140,140,0.85)',
							fontWeight: 600,
							textAlign: 'center',
							width: 100,
							opacity: interpolate(phase1, [0.7, 1], [0, 1], { extrapolateRight: 'clamp' }),
						}}
					>
						正电荷
					</div>
					<div
						style={{
							position: 'absolute',
							left: negativeX - 50,
							top: cy + chargeSize / 2 + 14,
							fontSize: 13 * TS,
							color: 'rgba(140,180,255,0.85)',
							fontWeight: 600,
							textAlign: 'center',
							width: 100,
							opacity: interpolate(phase1, [0.7, 1], [0, 1], { extrapolateRight: 'clamp' }),
						}}
					>
						负电荷
					</div>
				</>
			)}

			{/* 能量爆发光环 */}
			{burstRings}
			{burstOpacity > 0 && (
				<div
					style={{
						position: 'absolute',
						left: cx - burstRadius,
						top: cy - burstRadius,
						width: burstRadius * 2,
						height: burstRadius * 2,
						borderRadius: '50%',
						background: `radial-gradient(circle, rgba(255,240,200,${burstOpacity * 0.4}) 0%, transparent 70%)`,
						pointerEvents: 'none',
					}}
				/>
			)}

			{/* 顶部总标题 */}
			<div
				style={{
					position: 'absolute',
				top: 36,
				left: 0,
				right: 0,
				textAlign: 'center',
				fontSize: 22 * TS,
				color: 'rgba(200, 210, 240, 0.9)',
				fontWeight: 300,
				letterSpacing: 6 * TS,
					opacity: mainTitleOp,
				}}
			>
				电磁场 · THE ELECTROMAGNETIC FIELD
			</div>

			{/* 阶段叙事文本 */}
			<div
				style={{
					position: 'absolute',
					bottom: 100,
					left: 0,
					right: 0,
					textAlign: 'center',
					transform: `translateY(${titleY}px)`,
				}}
			>
				<div
					style={{
						fontSize: 32 * TS,
						color: '#fff',
						fontWeight: 700,
						letterSpacing: 8 * TS,
						opacity: titleOpacity,
						textShadow: `0 0 ${30 * S}px rgba(180,200,255,0.3)`,
					}}
				>
					{currentNarrative.text}
				</div>
				<div
					style={{
						fontSize: 17 * TS,
						color: 'rgba(200, 210, 240, 0.8)',
						fontWeight: 300,
						marginTop: 10 * TS,
						opacity: subtitleOpacity,
						letterSpacing: 1,
					}}
				>
					{currentNarrative.sub}
				</div>
			</div>

			{/* 底部阶段指示器 */}
			<div
				style={{
					position: 'absolute',
					bottom: 36,
					left: 0,
					right: 0,
					display: 'flex',
					justifyContent: 'center',
					gap: 24 * S,
					opacity: interpolate(totalProgress, [0.03, 0.1, 0.93, 1], [0, 0.6, 0.6, 0], {
						extrapolateRight: 'clamp',
					}),
				}}
			>
				{phaseNames.map((name, idx) => (
					<div
						key={name}
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 6 * S,
						}}
					>
						<div
							style={{
								width: 6 * S,
								height: 6 * S,
								borderRadius: '50%',
								background: idx <= phaseIndex ? 'rgba(255,220,160,0.9)' : 'rgba(255,255,255,0.2)',
								boxShadow: idx === phaseIndex ? `0 0 ${8 * S}px rgba(255,220,160,0.6)` : 'none',
								transition: 'background 0.3s',
							}}
						/>
						<span
							style={{
								fontSize: 14 * TS,
								color: idx <= phaseIndex ? 'rgba(255,220,160,0.85)' : 'rgba(255,255,255,0.25)',
								fontWeight: idx === phaseIndex ? 600 : 300,
							}}
						>
							{name}
						</span>
					</div>
				))}
			</div>
		</AbsoluteFill>
	);
};
